/**
 * The shell's handle on the signal worker (ADR-0027 D4.2).
 *
 * Everything crypto in the web client goes through this object, and this
 * object can do exactly what {@link SignalRequest} allows — which is how the
 * "no bridge capability reaches the signal module" rule stays true as the shell
 * grows. Adding an op here is a decision, not a refactor.
 */
import type {
  SignalRequest,
  SignalResponse,
  DeviceIdentity,
  WireBundle,
  RegistrationBundle,
  DecryptOutcome,
} from "./protocol.ts";

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };

/** `Omit<Union, "id">` collapses a union to its common keys — which for
 *  `SignalRequest` is `op` alone, and every call below then fails to type. This
 *  distributes the Omit across the members instead, so each op keeps its own
 *  fields and a typo in one is still a compile error. */
type Unsent<T> = T extends unknown ? Omit<T, "id"> : never;

export class SignalClient {
  private readonly worker: Worker;
  private readonly pending = new Map<number, Pending>();
  private nextId = 1;

  constructor(worker: Worker) {
    this.worker = worker;
    this.worker.onmessage = (ev: MessageEvent<SignalResponse>) => {
      const res = ev.data;
      const p = this.pending.get(res.id);
      if (!p) return;
      this.pending.delete(res.id);
      if (res.ok) p.resolve(res.result);
      else p.reject(new Error(res.error));
    };
    // A worker that dies takes every in-flight call with it. Rejecting them is
    // the difference between a visible failure and a UI that waits forever on
    // a promise nothing will ever settle.
    this.worker.onerror = (e: ErrorEvent) => this.failAll(e.message || "the signal worker stopped");
  }

  private failAll(reason: string): void {
    for (const [, p] of this.pending) p.reject(new Error(reason));
    this.pending.clear();
  }

  private send<T>(req: Unsent<SignalRequest>): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker.postMessage({ ...req, id } as SignalRequest);
    });
  }

  /** Open (or create) this browser's device. `isNew` is the shell's cue to run
   *  the link flow rather than assume it is online. */
  open(userId: string, wasmUrl: string): Promise<DeviceIdentity> {
    return this.send({ op: "open", userId, wasmUrl });
  }

  /** Re-address this browser's device onto the real account and the number the
   *  primary assigned, keeping the identity key the primary signed. */
  adopt(userId: string, deviceNumber: number, deviceRowId: string): Promise<{ deviceNumber: number; deviceRowId: string }> {
    return this.send({ op: "adopt", userId, deviceNumber, deviceRowId });
  }

  processBundle(peerName: string, peerDevice: number, bundle: WireBundle): Promise<void> {
    return this.send({ op: "processBundle", peerName, peerDevice, bundle });
  }

  encrypt(peerName: string, peerDevice: number, plaintext: Uint8Array): Promise<{ type: number; ciphertext: Uint8Array }> {
    return this.send({ op: "encrypt", peerName, peerDevice, plaintext });
  }

  /** Read one received envelope. Resolves with an OUTCOME — a failed decrypt
   *  is an ordinary event here, and the caller has to tell a held message from
   *  a dead one. See {@link DecryptOutcome}. */
  decrypt(peerName: string, peerDevice: number, type: number, ciphertext: Uint8Array): Promise<DecryptOutcome> {
    return this.send({ op: "decrypt", peerName, peerDevice, type, ciphertext });
  }

  safetyNumber(peerName: string, peerDevice: number): Promise<{ safetyNumber: string }> {
    return this.send({ op: "safetyNumber", peerName, peerDevice });
  }

  /** The public key material `POST /e2e/devices` needs. Call it once, after a
   *  primary has vouched for this browser — the prekeys it generates are
   *  persisted before it returns, so publishing what comes back is safe. */
  publishBundle(oneTimeCount = 20): Promise<RegistrationBundle> {
    return this.send({ op: "publishBundle", oneTimeCount });
  }

  /** Unlink this browser. The device and everything it can read are gone; a
   *  later link creates a NEW device, which correctly changes the safety
   *  number for every peer. */
  destroy(): Promise<void> {
    return this.send({ op: "destroy" });
  }

  terminate(): void {
    this.worker.terminate();
    this.failAll("the signal worker was terminated");
  }
}
